import { useState, useEffect } from "react";
import {
  Upload, Trash2, Plus, RefreshCw, Save, Edit, Edit3, X, FileText,
  AlertCircle, CheckCircle2, Home, Image, Camera, ExternalLink,
  Clock, Download, Eye, Receipt, Wallet, Euro, Loader2, Lock,
  Building2, MapPin, Users, Phone, Mail, Calendar, Shield,
  User, History, ArrowLeft, ArrowRight, Wrench, MessageSquare,
  ChevronDown, ChevronUp, Ban, Send, Copy, Smartphone, Key, QrCode,
  ShieldCheck, ShieldAlert, ShieldX, Thermometer, PenTool, FileCheck
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Input } from "../../../components/ui/input";
import { API, getAuthHeaders, formatMontant } from "./helpers";
import { toast } from "sonner";

// Défini hors du composant parent pour éviter unmount/remount à chaque re-render
// Utilise defaultValue (non-contrôlé) : React ne reset pas la valeur DOM sur re-render
const MoneyInput = ({ field, value, onChange, testId }) => (
  <Input
    data-testid={testId || `input-${field}`}
    type="text"
    inputMode="decimal"
    className="w-40 text-right"
    style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
    placeholder="0.00"
    defaultValue={value === 0 ? "" : value}
    onFocus={(e) => e.target.select()}
    onChange={(e) => onChange(field, e.target.value)}
  />
);

export function FinancementTab({ dossierId, tenantId, dossier }) {
  const isTEF = tenantId === "tef";
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [tvaRate, setTvaRate] = useState(10); // TVA 10% par défaut (travaux adaptation)

  useEffect(() => {
    loadFinancement();
  }, [dossierId]);

  const loadFinancement = async () => {
    try {
      const headers = getAuthHeaders(tenantId);
      if (isTEF) {
        const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/financement`, { headers, credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const pf = data.plan_financement || {};
          setPlanData(pf);
          initFormData(pf);
        }
      } else {
        const res = await fetch(`${API}/api/metier/devis?dossier_id=${dossierId}`, { headers, credentials: 'include' });
        if (res.ok) {
          const devis = await res.json();
          const principal = devis.find(d => d.is_principal) || devis[0];
          setPlanData(principal);
        }
      }
    } catch (err) {
      console.error("Erreur chargement financement:", err);
    } finally {
      setLoading(false);
    }
  };

  const initFormData = (pf) => {
    // Fallback : si le plan de financement est vide, pré-remplir avec les
    // montants déjà présents sur le dossier (champs Pydantic exposés par le
    // commit 37d6f5896) ou dans dossier.anah_snapshot. Évite à l'utilisateur
    // de ressaisir des montants déjà connus en base ("fuite" → montants
    // disponibles côté serveur mais invisibles dans le formulaire).
    const anahSnap = dossier?.anah_snapshot || {};
    const dossierTravauxHT  = dossier?.montant_travaux_ht;
    const dossierTravauxTTC = dossier?.montant_travaux ?? dossier?.montant_travaux_ttc;
    const dossierAideAnah   = anahSnap?.montant_aide ?? anahSnap?.aide_anah ?? anahSnap?.aide_mpa ?? dossier?.montant_aides;

    setFormData({
      montant_travaux_ht:    pf.montant_travaux_ht    || dossierTravauxHT  || "",
      montant_travaux_ttc:   pf.montant_travaux_ttc   || dossierTravauxTTC || "",
      honoraires_amo_ht:     pf.honoraires_amo_ht     || "",
      honoraires_moe_ht:     pf.honoraires_moe_ht     || "",
      aide_anah:             pf.aide_anah             || pf.aide_mpa || dossierAideAnah || "",
      aide_caisse_retraite:  pf.aide_caisse_retraite  || "",
      aide_departement:      pf.aide_departement      || "",
      aide_commune:          pf.aide_commune          || "",
      aide_region:           pf.aide_region           || "",
      autres_aides:          pf.autres_aides          || "",
      taux_prise_en_charge:  pf.taux_prise_en_charge  || "",
      pret_bancaire:         pf.pret_bancaire         || "",
      apport_personnel:      pf.apport_personnel      || "",
    });
  };

  const formatMontant = (val) => {
    if (val === null || val === undefined || val === "" || val === 0) return "-";
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(val);
  };

  const num = (v) => parseFloat(v) || 0;

  const totalDepensesHT = () => num(formData.montant_travaux_ht) + num(formData.honoraires_amo_ht) + num(formData.honoraires_moe_ht);

  // TTC auto-calculé depuis HT + TVA
  const calcTTC = () => Math.round(totalDepensesHT() * (1 + tvaRate / 100) * 100) / 100;

  const totalAides = () =>
    num(formData.aide_anah) + num(formData.aide_caisse_retraite) + num(formData.aide_departement) +
    num(formData.aide_commune) + num(formData.aide_region) + num(formData.autres_aides);

  const totalFinancements = () => totalAides() + num(formData.pret_bancaire) + num(formData.apport_personnel);

  // Équilibre = Dépenses TTC - Total financements (doit être 0)
  const equilibre = () => num(formData.montant_travaux_ttc) - totalFinancements();

  const handleFieldChange = (field, value) => {
    // Nettoyer la valeur pour n'avoir que des chiffres/points/virgules
    const cleaned = value.replace(/[^0-9.,]/g, '').replace(',', '.');
    const newData = { ...formData, [field]: cleaned };
    
    // Auto-calculer TTC quand on change un montant HT
    if (['montant_travaux_ht', 'honoraires_amo_ht', 'honoraires_moe_ht'].includes(field)) {
      const ht = num(field === 'montant_travaux_ht' ? cleaned : newData.montant_travaux_ht) +
                 num(field === 'honoraires_amo_ht' ? cleaned : newData.honoraires_amo_ht) +
                 num(field === 'honoraires_moe_ht' ? cleaned : newData.honoraires_moe_ht);
      newData.montant_travaux_ttc = Math.round(ht * (1 + tvaRate / 100) * 100) / 100;
    }

    // Auto-calculer taux de prise en charge
    const ttc = num(newData.montant_travaux_ttc);
    if (ttc > 0) {
      const aides = num(newData.aide_anah) + num(newData.aide_caisse_retraite) + num(newData.aide_departement) +
                    num(newData.aide_commune) + num(newData.aide_region) + num(newData.autres_aides);
      newData.taux_prise_en_charge = Math.round((aides / ttc) * 100);
    }

    setFormData(newData);
  };

  const handleTvaChange = (rate) => {
    setTvaRate(rate);
    const ht = num(formData.montant_travaux_ht) + num(formData.honoraires_amo_ht) + num(formData.honoraires_moe_ht);
    setFormData(prev => ({ ...prev, montant_travaux_ttc: Math.round(ht * (1 + rate / 100) * 100) / 100 }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      const body = {};
      for (const [k, v] of Object.entries(formData)) {
        body[k] = parseFloat(v) || 0;
      }
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/financement`, {
        method: "POST", headers, body: JSON.stringify(body), credentials: 'include'
      });
      if (res.ok) {
        toast.success("Plan de financement sauvegardé");
        setEditMode(false);
        loadFinancement();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur sauvegarde — vérifiez les données");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePDF = async () => {
    setGenerating(true);
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/financement/generer-pdf`, {
        method: "POST", headers, body: JSON.stringify({}), credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setGeneratedDoc(data);
        toast.success("Plan de financement PDF généré");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur génération");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setGenerating(false);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
      </div>
    );
  }

  // === TEF: Plan de Financement MPA ===
  if (isTEF) {
    const hasPlan = planData && (planData.montant_travaux_ttc || planData.aide_anah || planData.aide_mpa);
    const ttcValue = editMode ? num(formData.montant_travaux_ttc) : (planData?.montant_travaux_ttc || 0);
    const aidesValue = editMode ? totalAides() : (planData?.total_aides || 0);
    const finValue = editMode ? totalFinancements() : ((planData?.total_aides || 0) + (planData?.pret_bancaire || 0) + (planData?.apport_personnel || 0));
    const eqValue = editMode ? equilibre() : (ttcValue - finValue);

    return (
      <div className="rounded-xl p-6" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="plan-financement-tef">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
            <Wallet className="w-5 h-5" style={{ color: "var(--oh-stat-amber-text)" }} />
            Plan de Financement MPA (CERFA 13460-03)
          </h3>
          <div className="flex items-center gap-2">
            {hasPlan && !editMode && (
              <Button data-testid="edit-plan-btn" size="sm" variant="outline" onClick={() => setEditMode(true)}>
                <PenTool className="w-4 h-4 mr-1" /> Modifier
              </Button>
            )}
            {hasPlan && (
              <Button data-testid="generate-plan-pdf-btn" size="sm" className="bg-amber-600 hover:bg-amber-700 text-[var(--oh-accent-contrast)]" onClick={handleGeneratePDF} disabled={generating}>
                {generating ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Générer CERFA PDF
              </Button>
            )}
          </div>
        </div>

        {!hasPlan && !editMode ? (
          <div className="text-center py-8">
            <Wallet className="w-12 h-12 mx-auto mb-3 opacity-40" style={{ color: "var(--oh-text-muted)" }} />
            <p style={{ color: "var(--oh-text-muted)" }}>Aucun plan de financement</p>
            <Button size="sm" className="mt-4 bg-amber-600 hover:bg-amber-700 text-[var(--oh-accent-contrast)]" onClick={() => setEditMode(true)} data-testid="create-plan-btn">
              Créer le plan de financement
            </Button>
          </div>
        ) : (
          <>
            {/* Résumé visuel */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
              <div className="rounded-lg p-3 text-center" style={{ background: "var(--oh-bg-surface)" }}>
                <p className="text-xs mb-1" style={{ color: "var(--oh-text-muted)" }}>Dépenses TTC</p>
                <p className="text-xl font-medium" style={{ color: "var(--oh-text)" }} data-testid="montant-travaux-display">
                  {formatMontant(ttcValue)}
                </p>
              </div>
              <div className="rounded-lg p-3 text-center" style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)" }}>
                <p className="text-xs mb-1" style={{ color: "var(--oh-stat-green-text)" }}>Total aides</p>
                <p className="text-xl font-medium" style={{ color: "var(--oh-stat-green-text)" }} data-testid="total-aides-display">
                  {formatMontant(aidesValue)}
                </p>
              </div>
              <div className="rounded-lg p-3 text-center" style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)" }}>
                <p className="text-xs mb-1" style={{ color: "var(--oh-stat-blue-text)" }}>Total financements</p>
                <p className="text-xl font-medium" style={{ color: "var(--oh-stat-blue-text)" }}>
                  {formatMontant(finValue)}
                </p>
              </div>
              <div className="rounded-lg p-3 text-center" style={{ background: Math.abs(eqValue) < 1 ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)", border: `1px solid ${Math.abs(eqValue) < 1 ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)"}` }}>
                <p className="text-xs mb-1" style={{ color: Math.abs(eqValue) < 1 ? "var(--oh-stat-green-text)" : "var(--oh-stat-red-text)" }}>Équilibre</p>
                <p className="text-xl font-medium" style={{ color: Math.abs(eqValue) < 1 ? "var(--oh-stat-green-text)" : "var(--oh-stat-red-text)" }} data-testid="reste-charge-display">
                  {Math.abs(eqValue) < 1 ? "0,00 €" : formatMontant(eqValue)}
                </p>
              </div>
            </div>

            {/* Formulaire CERFA */}
            <div className="space-y-5">

              {/* DÉPENSES */}
              <div>
                <div className="rounded-t-lg px-4 py-2" style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)" }}>
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-sm" style={{ color: "var(--oh-stat-amber-text)" }}>DÉPENSES</h5>
                    {editMode && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>TVA :</span>
                        {[5.5, 10, 20].map(rate => (
                          <button
                            key={rate}
                            onClick={() => handleTvaChange(rate)}
                            className={`text-xs px-2 py-0.5 rounded transition-colors ${tvaRate === rate ? "bg-amber-500 text-[var(--oh-accent-contrast)]" : ""}`}
                            style={tvaRate !== rate ? { background: "var(--oh-bg-surface)", color: "var(--oh-text-muted)" } : {}}
                          >
                            {rate}%
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="rounded-b-lg p-4 space-y-3" style={{ border: "1px solid var(--oh-border)", borderTop: "none" }}>
                  {[
                    { field: 'montant_travaux_ht', label: 'Coût total des travaux subventionnables HT' },
                    { field: 'honoraires_amo_ht', label: "Coût prestation AMO HT" },
                    { field: 'honoraires_moe_ht', label: "Honoraires maîtrise d'œuvre HT" },
                  ].map(({ field, label }) => (
                    <div key={field} className="flex items-center justify-between">
                      <label className="text-sm flex-1" style={{ color: "var(--oh-text-secondary)" }}>{label}</label>
                      {editMode ? <MoneyInput field={field} value={formData[field]} onChange={handleFieldChange} /> : (
                        <span className="font-medium w-40 text-right" style={{ color: "var(--oh-text)" }}>{formatMontant(planData?.[field])}</span>
                      )}
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2" style={{ borderTop: "1px solid var(--oh-border)" }}>
                    <span className="font-medium text-sm" style={{ color: "var(--oh-text)" }}>Total dépenses HT</span>
                    <span className="font-medium w-40 text-right" style={{ color: "var(--oh-text)" }}>
                      {formatMontant(editMode ? totalDepensesHT() : ((planData?.montant_travaux_ht||0)+(planData?.honoraires_amo_ht||0)+(planData?.honoraires_moe_ht||0)))}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 rounded" style={{ background: "rgba(245,158,11,0.06)" }}>
                    <span className="font-medium text-sm" style={{ color: "var(--oh-stat-amber-text)" }}>Montant global TTC {editMode ? `(TVA ${tvaRate}%)` : ""}</span>
                    {editMode ? (
                      <span className="font-medium w-40 text-right" style={{ color: "var(--oh-stat-amber-text)" }} data-testid="montant-ttc-auto">
                        {formatMontant(num(formData.montant_travaux_ttc))}
                      </span>
                    ) : (
                      <span className="font-medium w-40 text-right" style={{ color: "var(--oh-stat-amber-text)" }}>{formatMontant(planData?.montant_travaux_ttc)}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* AIDES PUBLIQUES */}
              <div>
                <div className="rounded-t-lg px-4 py-2" style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)" }}>
                  <h5 className="font-medium text-sm" style={{ color: "var(--oh-stat-green-text)" }}>FINANCEMENTS PUBLICS</h5>
                </div>
                <div className="rounded-b-lg p-4 space-y-3" style={{ border: "1px solid var(--oh-border)", borderTop: "none" }}>
                  {[
                    { field: 'aide_anah', label: "Aide de l'Anah (MaPrimeAdapt')", highlight: true },
                    { field: 'aide_commune', label: 'Commune ou EPCI' },
                    { field: 'aide_departement', label: 'Conseil départemental' },
                    { field: 'aide_region', label: 'Conseil régional' },
                    { field: 'aide_caisse_retraite', label: 'Caisses de retraite' },
                    { field: 'autres_aides', label: 'Autres aides publiques' },
                  ].map(({ field, label, highlight }) => (
                    <div key={field} className={`flex items-center justify-between ${highlight ? 'p-2 rounded' : ''}`} style={highlight ? { background: "rgba(16,185,129,0.04)" } : {}}>
                      <label className="text-sm flex-1" style={{ color: "var(--oh-text-secondary)" }}>{label}</label>
                      {editMode ? <MoneyInput field={field} value={formData[field]} onChange={handleFieldChange} /> : (
                        <span className="font-medium w-40 text-right" style={{ color: "var(--oh-stat-green-text)" }}>{formatMontant(planData?.[field])}</span>
                      )}
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2" style={{ borderTop: "1px solid rgba(16,185,129,0.2)" }}>
                    <span className="font-medium text-sm" style={{ color: "var(--oh-stat-green-text)" }}>Total aides publiques</span>
                    <span className="font-medium w-40 text-right" style={{ color: "var(--oh-stat-green-text)" }}>{formatMontant(editMode ? totalAides() : (planData?.total_aides || 0))}</span>
                  </div>
                </div>
              </div>

              {/* FINANCEMENT PRIVÉ */}
              <div>
                <div className="rounded-t-lg px-4 py-2" style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)" }}>
                  <h5 className="font-medium text-sm" style={{ color: "var(--oh-stat-blue-text)" }}>FINANCEMENT PRIVÉ</h5>
                </div>
                <div className="rounded-b-lg p-4 space-y-3" style={{ border: "1px solid var(--oh-border)", borderTop: "none" }}>
                  {[
                    { field: 'pret_bancaire', label: 'Prêt bancaire' },
                    { field: 'apport_personnel', label: 'Apport de fonds personnels' },
                  ].map(({ field, label }) => (
                    <div key={field} className="flex items-center justify-between">
                      <label className="text-sm flex-1" style={{ color: "var(--oh-text-secondary)" }}>{label}</label>
                      {editMode ? <MoneyInput field={field} value={formData[field]} onChange={handleFieldChange} /> : (
                        <span className="font-medium w-40 text-right" style={{ color: "var(--oh-text)" }}>{formatMontant(planData?.[field])}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* ÉQUILIBRE FINANCIER */}
              <div className="rounded-lg p-4 space-y-2" style={{ background: Math.abs(eqValue) < 1 ? "rgba(16,185,129,0.06)" : "rgba(239,68,68,0.06)", border: `1px solid ${Math.abs(eqValue) < 1 ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)"}` }}>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm" style={{ color: "var(--oh-text)" }}>Coût total TTC</span>
                  <span className="font-medium" style={{ color: "var(--oh-stat-amber-text)" }}>{formatMontant(ttcValue)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm" style={{ color: "var(--oh-text)" }}>Total financements (public + privé)</span>
                  <span className="font-medium" style={{ color: "var(--oh-stat-blue-text)" }}>{formatMontant(finValue)}</span>
                </div>
                <div className="flex justify-between items-center pt-2" style={{ borderTop: `1px solid ${Math.abs(eqValue) < 1 ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"}` }}>
                  <span className="font-medium" style={{ color: Math.abs(eqValue) < 1 ? "var(--oh-stat-green-text)" : "var(--oh-stat-red-text)" }}>
                    {Math.abs(eqValue) < 1 ? "Équilibre financier atteint" : "Reste à financer"}
                  </span>
                  <span className="font-medium text-lg" style={{ color: Math.abs(eqValue) < 1 ? "var(--oh-stat-green-text)" : "var(--oh-stat-red-text)" }}>
                    {Math.abs(eqValue) < 1 ? "0,00 €" : formatMontant(eqValue)}
                  </span>
                </div>
              </div>

              {/* Boutons */}
              {editMode && (
                <div className="flex gap-2 mt-4">
                  <Button data-testid="save-plan-btn" className="bg-emerald-600 hover:bg-emerald-700 text-[var(--oh-accent-contrast)]" onClick={handleSave} disabled={saving}>
                    {saving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    Sauvegarder
                  </Button>
                  <Button variant="outline" onClick={() => { setEditMode(false); loadFinancement(); }}>
                    Annuler
                  </Button>
                </div>
              )}
            </div>

            {/* Document généré */}
            {generatedDoc && (
              <div className="mt-4 p-3 rounded-lg flex items-center justify-between" style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)" }}>
                <div className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5" style={{ color: "var(--oh-stat-green-text)" }} />
                  <span className="text-sm" style={{ color: "var(--oh-stat-green-text)" }}>CERFA généré : {generatedDoc.filename}</span>
                </div>
                <Button size="sm" variant="outline" data-testid="download-cerfa-btn" onClick={async () => {
                  try {
                    const headers = getAuthHeaders(tenantId);
                    const res = await fetch(`${API}${generatedDoc.download_url}`, { headers, credentials: 'include' });
                    if (res.ok) {
                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = generatedDoc.filename || 'CERFA_13460_03.pdf';
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                    } else { toast.error("Erreur téléchargement"); }
                  } catch (e) { toast.error("Erreur réseau"); }
                }}>
                  <Download className="w-4 h-4 mr-1" /> Télécharger
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // === CLIMAT: Affichage classique basé sur devis principal ===
  const devisPrincipal = planData;
  const montantTravaux = devisPrincipal?.total_ttc || dossier?.montant_travaux || 0;
  const montantAides = devisPrincipal?.snapshot_aides?.total_aides || dossier?.montant_aides || 0;
  const resteAChargeClimat = montantTravaux - montantAides;

  return (
    <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl p-6">
      <h3 className="text-[var(--oh-text)] font-medium mb-6">Plan de Financement</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 text-center">
          <p className="text-[var(--oh-text-muted)] text-sm mb-1">Montant travaux TTC</p>
          <p className="text-2xl font-medium text-[var(--oh-text)]">{formatMontant(montantTravaux)}</p>
        </div>
        <div className="bg-[var(--oh-success-bg)] rounded-lg p-4 text-center border border-[var(--oh-success-border)]">
          <p className="text-[var(--oh-success-text)] text-sm mb-1">Total aides</p>
          <p className="text-2xl font-medium text-[var(--oh-success-text)]">{formatMontant(montantAides)}</p>
        </div>
        <div className="bg-[var(--oh-info-bg)] rounded-lg p-4 text-center border border-[var(--oh-info-border)]">
          <p className="text-[var(--oh-info-text)] text-sm mb-1">Reste à charge</p>
          <p className="text-2xl font-medium text-[var(--oh-info-text)]">{formatMontant(resteAChargeClimat)}</p>
        </div>
      </div>

      {devisPrincipal && (
        <div className="mt-6 pt-6 border-t border-[var(--oh-border)]">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-[var(--oh-text)] font-medium">Devis principal</h4>
            <Badge className="bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]">{devisPrincipal.numero}</Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-[var(--oh-text-muted)]">Total HT</p><p className="text-[var(--oh-text)] font-medium">{formatMontant(devisPrincipal.total_ht)}</p></div>
            <div><p className="text-[var(--oh-text-muted)]">TVA</p><p className="text-[var(--oh-text)] font-medium">{formatMontant(devisPrincipal.total_tva)}</p></div>
            <div><p className="text-[var(--oh-text-muted)]">Total TTC</p><p className="text-[var(--oh-text)] font-medium">{formatMontant(devisPrincipal.total_ttc)}</p></div>
            <div><p className="text-[var(--oh-text-muted)]">Statut</p><Badge variant="outline">{devisPrincipal.statut}</Badge></div>
          </div>
        </div>
      )}

      {!devisPrincipal && (
        <div className="text-center py-8">
          <Euro className="w-12 h-12 mx-auto text-[var(--oh-text-secondary)] mb-3" />
          <p className="text-[var(--oh-text-muted)]">Aucun devis associé</p>
          <p className="text-[var(--oh-text-secondary)] text-sm mt-1">Créez un devis pour voir le plan de financement</p>
        </div>
      )}
    </div>
  );
}
